import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Button, useTheme } from "react-native-elements";
import { PlayerContext } from "../backend/PlayerContext";
import { retire } from "../backend/Retire";
import {
  School,
  getMaxOfMaxPrimarySchoolLevels,
  getSchoolLevel,
  maximumSchoolLevelProductionMultiplierDx,
  maximumSchoolLevelResearchMultiplier,
} from "../backend/spells/ElementsAndSchools";
import { Storylines } from "../backend/storylines/Storylines";
import { clone } from "../utils/CoreUtils";
import { formatNumber } from "../utils/FormattingUtils";
import { Markdown } from "./utility/Markdown";
import { Text } from "./utility/Text";

import { hasGlobalFlagIncludingNextRun } from "../backend/Flags";
import UseTimePiecesResearch from "../backend/actions/campus/UseTimePiecesResearch";
import { getEventMessageById } from "../backend/events/Events";
import { calculateMemoryChamberExplorationMultiplier } from "../backend/events/storyline/MemoryChamber";
import {
  isStuckOnBoss,
  mplIsTooHigh,
  mplIsTooLow,
} from "../backend/exploration/dungeons/Dungeons";
import { VisibilityContext } from "./utility/VisibilityContext";

export default function Retirement() {
  const playerContext = useContext(PlayerContext);

  const modifiedContext = retire(clone(playerContext), true);
  modifiedContext.isFake = true;
  const newResearchMultiplier =
    maximumSchoolLevelResearchMultiplier(modifiedContext);
  const newProductionMultiplier =
    maximumSchoolLevelProductionMultiplierDx(modifiedContext);
  const oldResearchMultiplier =
    maximumSchoolLevelResearchMultiplier(playerContext);
  const oldProductionMultiplier =
    maximumSchoolLevelProductionMultiplierDx(playerContext);
  const researchDeltaPct =
    ((newResearchMultiplier - oldResearchMultiplier) * 100.0) /
    oldResearchMultiplier;
  const productionDeltaPct =
    ((newProductionMultiplier - oldProductionMultiplier) * 100.0) /
    oldProductionMultiplier;
  const stuckOnBoss = isStuckOnBoss(playerContext);

  const newStorylines = Storylines.getAll().filter(
    (storyline) =>
      !storyline.isBonusActive(playerContext) &&
      storyline.isCompleted(playerContext),
  );

  let memoryChamberMessage = "";
  const completedMemoryChamber = hasGlobalFlagIncludingNextRun(
    playerContext,
    "memory_chamber_completed",
  );
  const completedUpgradedMemoryChamber = hasGlobalFlagIncludingNextRun(
    playerContext,
    "upgraded_memory_chamber_completed",
  );
  if (completedMemoryChamber && !completedUpgradedMemoryChamber) {
    const memoryChamberMultiplierBefore =
      calculateMemoryChamberExplorationMultiplier(playerContext);
    const memoryChamberMultiplierAfter =
      calculateMemoryChamberExplorationMultiplier(modifiedContext);
    const memoryChamberMultiplierDeltaPct =
      ((memoryChamberMultiplierAfter - memoryChamberMultiplierBefore) * 100.0) /
      memoryChamberMultiplierBefore;
    memoryChamberMessage = ` Your Memory Chamber will also reduce the required exploration for previously completed areas to **${formatNumber(
      memoryChamberMultiplierAfter * 100,
      { showDecimals: false },
    )}% (${
      memoryChamberMultiplierDeltaPct != 0
        ? formatNumber(memoryChamberMultiplierDeltaPct, {
            showDecimals: false,
          }) + "%"
        : "no change"
    })**.`;
  }

  let storylineBonusMessage = "";
  if (newStorylines.length > 0) {
    storylineBonusMessage = ` The following Storyline bonuses will also fully activate:
    
${newStorylines
  .map(
    (storyline) =>
      `* **${storyline.name}**: ${storyline.getBonusDescription(
        playerContext,
      )}`,
  )
  .join("\n")}`;
  }

  const retirementEffects = `**If you retire now, your research multiplier will become ${formatNumber(
    newResearchMultiplier,
    { showDecimals: true },
  )}x (+${formatNumber(researchDeltaPct, {
    showDecimals: true,
  })}%) and your production and loot multiplier will become ${formatNumber(
    newProductionMultiplier,
    { showDecimals: true },
  )}x (+${formatNumber(productionDeltaPct, {
    showDecimals: true,
  })}%).**${memoryChamberMessage}${storylineBonusMessage}`;

  const text = `You have built up enough knowledge that you feel it might be enough. You consider retirement.

If you retire, **you will reset the game.** Everything will revert back to its initial state, except:
* Any unlocked schools or elements will stay unlocked;
* Any bonuses you obtained from "(Storyline)" events will remain;
* You will gain a boost to research, building production and loot based on the maximum level you have ever attained on your primary school (MPL), as well as on the sum of the MPLs you have attained on each school you have unlocked.

When you retire, you will be able to choose a new primary school and element.

${retirementEffects}

*Do you want to retire?*`;

  const [hasClicked, setHasClicked] = useState(false);
  const visible = useContext(VisibilityContext);
  useEffect(() => {
    if (!visible) {
      setHasClicked(false);
    }
  }, [visible]);
  const showTimePiecesWarning =
    UseTimePiecesResearch.isVisible(playerContext) &&
    UseTimePiecesResearch.isEnabled(playerContext);
  const showOngoingEventsWarning = Object.values(
    playerContext.eventOccurrences,
  ).some((event) => {
    return (
      event.currentMessageId &&
      !getEventMessageById(event.currentMessageId).isTerminal()
    );
  });
  const showMPLTooLowWarning =
    mplIsTooLow(playerContext) &&
    getSchoolLevel(
      playerContext,
      playerContext.primarySchool ?? School.Conjuration,
    ) >
      getMaxOfMaxPrimarySchoolLevels(playerContext) + 4;
  const showMPLTooHighWarning = mplIsTooHigh(playerContext);
  const theme = useTheme();

  return (
    <View style={styles.container}>
      <Text h1>Retirement</Text>
      <View style={styles.explanation}>
        <Markdown>{text}</Markdown>
      </View>
      {showMPLTooLowWarning && (
        <View style={styles.warning}>
          <Text style={{ fontStyle: "italic" }}>
            It looks like your multipliers might be too low for your current
            progression in Exploration, and your retirement bonus seems
            reasonably high. This might be a good time to retire!
          </Text>
        </View>
      )}
      {showMPLTooHighWarning && (
        <View style={styles.warning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            Warning: Your multipliers seem to be very high for your current
            progression in Exploration. You might benefit more by making more
            progress in Exploration. Are you sure you want to retire?
          </Text>
        </View>
      )}
      {stuckOnBoss && (
        <View style={styles.warning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            Warning: It looks like you could be stuck on a boss. If you cannot
            defeat a boss, and you have already tried retiring once to defeat it
            without success, you may possibly have better results by thinking
            harder about how to defeat the boss rather than starting over again.
            Are you sure you want to retire?
          </Text>
        </View>
      )}
      {showTimePiecesWarning && (
        <View style={styles.warning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            Warning: It looks like you still have many unused Time Pieces!
            Consider using them up with "Use Time Pieces (Research)" (or in
            other ways) before retiring, as they will be gone when you retire!
          </Text>
        </View>
      )}
      {showOngoingEventsWarning && (
        <View style={styles.warning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            Warning: You still have ongoing events! Are you sure you want to
            retire?
          </Text>
        </View>
      )}
      <View style={styles.buttonContainer}>
        {!hasClicked && (
          <Button title="Retire" onPress={() => setHasClicked(true)} />
        )}
        {hasClicked && (
          <Button
            title="Are you sure? Press again to confirm"
            onPress={() => {
              playerContext.apply((state) => retire(state, false));
            }}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  explanation: {
    marginTop: 16,
  },
  buttonContainer: {
    marginTop: 16,
  },
  warning: {
    marginTop: 8,
  },
});
